import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { Minus, Plus, Trash2, ShoppingBag, MessageCircle, Phone as PhoneIcon, Truck } from 'lucide-react';
import { getCartWhatsAppUrl } from '@/lib/whatsapp';

const CartDrawer = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeFromCart, totalPrice, isOpen, setIsOpen, clearCart } = useCart();
  const minOrder = 1000;
  const canCheckout = totalPrice >= minOrder;

  const whatsappUrl = getCartWhatsAppUrl(
    items.map(i => ({ name: i.product.name, price: i.product.price, qty: i.quantity }))
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" /> Your Cart ({items.length})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <ShoppingBag className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">Your cart is empty</p>
            <Button className="mt-4" onClick={() => setIsOpen(false)}>Continue Shopping</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-3 py-4">
              {items.map(item => (
                <div key={item.product.id} className="flex gap-3 p-3 rounded-lg bg-muted/50">
                  <img src={item.product.image} alt={item.product.name} className="w-16 h-16 object-cover rounded-md" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.product.name}</p>
                    <p className="text-sm font-bold text-primary">₹{item.product.price.toLocaleString('en-IN')}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product.id, item.quantity - 1)}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product.id, item.quantity + 1)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto text-destructive" onClick={() => removeFromCart(item.product.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-heading font-semibold">Total</span>
                <span className="font-heading font-bold text-lg">₹{totalPrice.toLocaleString('en-IN')}</span>
              </div>

              {!canCheckout && (
                <p className="text-xs text-destructive text-center">Minimum order ₹{minOrder.toLocaleString('en-IN')}. Add ₹{(minOrder - totalPrice).toLocaleString('en-IN')} more.</p>
              )}

              <div className="space-y-2">
                <Button className="w-full gap-2" disabled={!canCheckout} onClick={() => { setIsOpen(false); navigate('/checkout'); }}>
                  <Truck className="h-4 w-4" /> Cash on Delivery
                </Button>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full gap-2 border-success text-success hover:bg-success hover:text-primary-foreground">
                    <MessageCircle className="h-4 w-4" /> Order via WhatsApp
                  </Button>
                </a>
                <a href="tel:+919679200493" className="block">
                  <Button variant="outline" className="w-full gap-2">
                    <PhoneIcon className="h-4 w-4" /> Call to Order
                  </Button>
                </a>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
